import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { PWAProvider } from "@/contexts/PWAContext";
import { PageTransition } from "@/components/animations/PageTransition";
import { RootLayout } from "@/components/layout/RootLayout";
import { lazy, Suspense, type ReactNode } from "react";
import { AdminRoute } from "@/components/admin/AdminRoute";
import { useTimeBasedWarmth } from "@/hooks/useTimeBasedWarmth";

// Import AppShell eagerly - shell must never suspend or it causes remounts
import { AppShell } from "@/components/layout/AppShell";

// Import Landing directly for faster LCP (it's the primary entry point)
import Landing from "./pages/Landing";
// Lazy load other pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Products = lazy(() => import("./pages/Products"));
const Settings = lazy(() => import("./pages/Settings"));
const Delivery = lazy(() => import("./pages/Delivery"));
const PublicDeliveryView = lazy(() => import("./pages/PublicDeliveryView"));
const PublicOrderIntake = lazy(() => import("./pages/PublicOrderIntake"));
const Register = lazy(() => import("./pages/Register"));
const Explore = lazy(() => import("./pages/Explore"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Archive = lazy(() => import("./pages/Archive"));
const Customers = lazy(() => import("./pages/Customers"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const ApplicationsPage = lazy(() => import("./pages/admin/ApplicationsPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const UserHistoryPage = lazy(() => import("./pages/admin/UserHistoryPage"));
const StatisticsPage = lazy(() => import("./pages/admin/StatisticsPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Overlay pages - direct imports for instant-open experience
// No lazy loading = no Suspense skeleton = immediate content
import NewOrder from "./pages/orders/NewOrder";
import BulkUpload from "./pages/orders/BulkUpload";
import OrderDetail from "./pages/orders/OrderDetail";
import CustomerDetail from "./pages/customers/CustomerDetail";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:  2 * 60 * 1000, // 30 seconds - data stays fresh
      gcTime: 10 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false, // Don't refetch when tab gains focus
      retry: 1, // Only retry once on failure
    },
  },
});

// Component to apply time-based warmth effect
function TimeBasedWarmthProvider({ children }: { children: ReactNode }) {
  useTimeBasedWarmth();
  return <>{children}</>;
}


// Minimal fallback for public pages during chunk loading
const PublicPageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-muted-foreground text-sm">იტვირთება...</p>
    </div>
  </div>
);

// Create data router for useBlocker support
// NAVIGATION LAW:
// - Public routes: PageTransition allowed
// - App routes: Wrapped in AppShell (persistent shell, no remount)
// - Overlays: Self-animate via OverlayPage component
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // === PUBLIC ROUTES - Full transition allowed ===
      { path: "/", element: <PageTransition><Landing /></PageTransition> },
      { path: "/explore", element: <PageTransition><Explore /></PageTransition> },
      { path: "/register", element: <PageTransition><Register /></PageTransition> },
      { path: "/reset-password", element: <PageTransition><ResetPassword /></PageTransition> },
      { path: "/thank-you", element: <PageTransition><ThankYou /></PageTransition> },
      { path: "/privacy-policy", element: <PageTransition><PrivacyPolicy /></PageTransition> },
      { path: "/terms-of-service", element: <PageTransition><TermsOfService /></PageTransition> },
      
      // === APP ROUTES - Wrapped in persistent AppShell ===
      {
        element: <AppShell />,
        children: [
          {
            path: "/orders",
            element: <Index />,
            children: [
              { path: "new", element: <NewOrder /> },
              { path: "bulk-upload", element: <BulkUpload /> },
              { path: ":id", element: <OrderDetail /> },
            ],
          },
          {
            path: "/customers",
            element: <Customers />,
            children: [
              { path: ":customerId", element: <CustomerDetail /> },
            ],
          },
          { path: "/products", element: <Products /> },
          { path: "/archive", element: <Archive /> },
          { path: "/delivery", element: <Delivery /> },
          { path: "/dashboard", element: <Dashboard /> },
          { path: "/settings", element: <Settings /> },
        ],
      },
      
      // === EXTERNAL/PUBLIC - No shell, with Suspense for chunk load safety ===
      { path: "/d/:shortcode", element: <Suspense fallback={<PublicPageFallback />}><PublicDeliveryView /></Suspense> },
      { path: "/o/:shortcode", element: <Suspense fallback={<PublicPageFallback />}><PublicOrderIntake /></Suspense> },
      { path: "/c/:shortcode", element: <Suspense fallback={<PublicPageFallback />}><PublicOrderIntake /></Suspense> },
      
      // === ADMIN - Separate shell ===
      {
        path: "/admin",
        element: <AdminRoute><PageTransition><AdminLayout /></PageTransition></AdminRoute>,
        children: [
          { index: true, element: <ApplicationsPage /> },
          { path: "users", element: <UsersPage /> },
          { path: "statistics", element: <StatisticsPage /> },
          { path: "history", element: <UserHistoryPage /> },
        ],
      },
      
      // Catch-all route
      { path: "*", element: <PageTransition><NotFound /></PageTransition> },
    ],
  },
]);
const App = () => (
  <ErrorBoundary>
    <PWAProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <OnboardingProvider>
              <TimeBasedWarmthProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  {/* No Suspense here - shell handles its own content fallback */}
                  <RouterProvider router={router} />
                </TooltipProvider>
              </TimeBasedWarmthProvider>
            </OnboardingProvider>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </PWAProvider>
  </ErrorBoundary>
);

export default App;
